/**
 * Get dealer code from store state
 * @param {Object} store - Vuex store
 * @returns {string} Dealer code
 */
/* eslint-disable complexity */
function getDealerCodeFromStore(store) {
	const dealer = store.state.dealer;
	const selected = dealer && dealer.selected;

	if (!selected) {
		return '';
	}

	return selected.code || selected.dealerCode || '';
}
/* eslint-enable complexity */

/**
 * Get dealer code from dataLayer
 * @returns {string} Dealer code
 */
function getDealerCodeFromDataLayer() {
	const dataLayer = window.dataLayer;
	const firstEntry = dataLayer && dataLayer[0];

	if (!firstEntry) {
		return '';
	}

	return firstEntry.dealerCode || '';
}

/**
 * Update chat with dealer code
 * @param {string} dealerCode - Dealer code to sync
 * @returns {void}
 */
function updateChat(dealerCode) {
	if (window.updateChatDealerCode) {
		window.updateChatDealerCode(dealerCode);
	}
}

/**
 * Vuex plugin to automatically sync dealer code and URL changes with Salesforce chat.
 * This eliminates the need for manual window.updateChatDealerCode() calls on every page.
 *
 * Handles two scenarios:
 * 1. Dealer selection changes - syncs when user selects a new dealer
 * 2. Route navigation - syncs URL changes as user navigates through the funnel
 *
 * The duplicate check in chat.js ensures updates only happen when dealer code OR URL actually changes.
 * @param {Object} router - Vue Router instance
 * @returns {Function} Vuex plugin function
 */
/**
 * Handle dealer selection mutation
 * @param {Object} mutation - Vuex mutation
 * @returns {void}
 */
/* eslint-disable complexity */
function handleDealerSelection(mutation) {
	if (mutation.type !== 'dealer/selected') {
		return;
	}

	if (!mutation.payload) {
		return;
	}

	const dealerCode = mutation.payload.code || mutation.payload.dealerCode;

	if (dealerCode) {
		updateChat(dealerCode);
	}
}
/* eslint-enable complexity */

/**
 * Handle route change
 * Small delay needed to ensure window.location.href is fully updated before chat sync
 * @param {Object} store - Vuex store
 * @returns {void}
 */
function handleRouteChange(store) {
	setTimeout(() => {
		const dealerCode = getDealerCodeFromStore(store) || getDealerCodeFromDataLayer();

		updateChat(dealerCode);
	}, 50);
}

export default function createChatSyncPlugin(router) {
	return (store) => {
		// Sync dealer code when it changes in the Vuex store
		store.subscribe((mutation) => {
			handleDealerSelection(mutation);
		});

		// Sync URL on route changes (chat.js automatically captures window.location.href)
		if (router) {
			router.afterEach(() => {
				handleRouteChange(store);
			});
		}
	};
}
